import styled, { css } from 'styled-components';

import { Loader as BaseLoader } from '@/components/Loader';
import { absoluteCenter, conditionalStyle } from '@/theme';
import type { Theme } from '@/theme/types';

import type { ButtonProps } from './Button';

export const getVariantStyles = (
  variant: ButtonProps['variant'],
  theme: Theme,
  disabled: ButtonProps['disabled'],
) => {
  switch (variant) {
    case 'outlined':
      return css`
        border: 2px solid ${theme.colors.black};
        background-color: ${theme.colors.white};
        color: ${theme.colors.black};

        :hover {
          background-color: ${theme.colors.primaryDark};
          color: ${theme.colors.white};
        }
      `;

    default:
      return css`
        border: 2px solid ${theme.colors.primary};
        background-color: ${theme.colors.primary};
        color: ${theme.colors.black};

        :hover {
          color: ${theme.colors.black};
          background-color: ${theme.colors.white};
          border: 2px solid ${theme.colors.black};
        }

        ${disabled &&
        css`
          opacity: 0.4;
          pointer-events: none;
        `}
      `;
  }
};

export const getSizeStyles = (size: ButtonProps['size'], theme: Theme) => {
  switch (size) {
    case 'large':
      return css`
        padding: ${theme.spacing(1.8, 10.25)};
      `;

    default:
      return css`
        padding: ${theme.spacing(1.8, 2)};
      `;
  }
};

export const Button = styled.button<
  Pick<ButtonProps, 'variant' | 'disabled' | 'size' | 'isFullWidth'> & {
    $isFullWidthMobile: ButtonProps['isFullWidthMobile'];
    $isProcessing: ButtonProps['isProcessing'];
    $isUpperCase: ButtonProps['isUpperCase'];
  }
>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  ${({
    $isFullWidthMobile,
    $isProcessing,
    $isUpperCase,
    disabled,
    isFullWidth,
    size,
    theme,
    variant,
  }) => css`
    font-weight: ${theme.fontWeights.bold};
    font-size: ${theme.fontSizes.button};
    transition: ${theme.transitions.create([
      'background-color',
      'opacity',
      'color',
    ])};
    &:focus,
    &:active,
    &:focus:active {
      border: 2px solid ${theme.colors.black};
    }

    ${conditionalStyle($isProcessing, 'cursor', 'wait')}
    ${conditionalStyle($isUpperCase, 'text-transform', 'uppercase')}
    ${conditionalStyle($isProcessing || disabled, 'pointer-events', 'none')}
    ${conditionalStyle(isFullWidth, 'width', '100%')}
    
    ${getVariantStyles(variant!, theme, disabled)}
    ${getSizeStyles(size, theme)}
    
    ${$isFullWidthMobile &&
    css`
      ${theme.media.md.down} {
        width: 100%;
      }
    `}
  `}
`;

export const Loader = styled(BaseLoader)`
  ${absoluteCenter}
`;

export const Content = styled.span<Pick<ButtonProps, 'isProcessing'>>`
  ${({ isProcessing }) =>
    isProcessing &&
    css`
      opacity: 0;
    `}
`;
